import { useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  EntitySelectorBaseProps,
  useEntitySearch,
  EntityDropdownContent,
} from "@/components/EntitySelectorBase";
import { EntitySelectorTriggerContent } from "@/components/entitySelector/EntitySelectorTriggerContent";

interface SingleEntitySelectorProps<T extends { id: string }>
  extends EntitySelectorBaseProps<T> {
  currentlySelectedId: string | null;
  onSelectId: (id: string | null) => void;
}

export function SingleEntitySelector<T extends { id: string }>({
  entities,
  listLoading,
  fetchEntities,
  getDisplayName,
  getSecondaryInfo,
  renderExtraInfo,
  currentlySelectedId,
  onSelectId,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  className,
  disabled = false,
  variant = "default",
  size = "default",
  excludeId,
  entityLabel = "items",
  onCreateNew,
  createNewLabel,
  resultLimit,
  renderRowMeta,
  renderRowPrice,
  renderTriggerValue,
  createRows,
  onLoadMore,
  hasMore,
  isLoadingMore,
  popoverMinWidth,
  pinnedEntityIds,
  pinnedGroupLabel,
}: SingleEntitySelectorProps<T>) {
  const {
    open,
    setOpen,
    searchValue,
    setSearchValue,
    isSearching,
    displayedEntityIds,
    hasLoadedResults,
    loadMore,
    closeSelector,
  } = useEntitySearch({ fetchEntities, listLoading, onLoadMore });

  const selectedEntity = currentlySelectedId
    ? entities.get(currentlySelectedId)
    : null;

  const handleSelect = useCallback(
    (id: string) => {
      if (id === currentlySelectedId) {
        onSelectId(null);
      } else {
        onSelectId(id);
      }
      closeSelector();
    },
    [currentlySelectedId, onSelectId, closeSelector],
  );

  const isGhost = variant === "ghost";

  const selectedExtraInfo = selectedEntity
    ? renderExtraInfo?.(selectedEntity)
    : null;

  const triggerContent = (
    <EntitySelectorTriggerContent
      displayName={selectedEntity ? getDisplayName(selectedEntity) : undefined}
      placeholder={placeholder}
      extraInfo={selectedExtraInfo}
      value={selectedEntity ? renderTriggerValue?.(selectedEntity) : undefined}
      singleLine={renderTriggerValue !== undefined}
    />
  );

  return (
    <div className={cn("w-full", className)}>
      <Popover open={open} onOpenChange={setOpen} modal={true}>
        <PopoverTrigger asChild>
          {isGhost ? (
            <button
              type="button"
              disabled={disabled}
              className={cn(
                "flex min-h-8 w-full items-center justify-between rounded-md bg-transparent px-2 py-1 text-sm text-left transition-colors",
                "border border-transparent",
                "hover:border-input hover:bg-background",
                "focus:border-ring focus:bg-background focus:outline-none focus:ring-1 focus:ring-ring",
                "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-transparent disabled:hover:bg-transparent",
                !selectedEntity && "text-muted-foreground",
              )}
            >
              {triggerContent}
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </button>
          ) : (
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              className={cn(
                "w-full justify-between text-left font-normal",
                size === "sm" && "h-8 text-sm",
              )}
            >
              {triggerContent}
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent
          className="p-0"
          align="start"
          style={{
            width: "var(--radix-popover-trigger-width)",
            minWidth: popoverMinWidth,
          }}
        >
          <EntityDropdownContent
            entities={entities}
            displayedEntityIds={displayedEntityIds}
            excludeId={excludeId}
            isSearching={isSearching}
            hasLoadedResults={hasLoadedResults}
            isItemSelected={(id) => id === currentlySelectedId}
            onSelectItem={handleSelect}
            getDisplayName={getDisplayName}
            getSecondaryInfo={getSecondaryInfo}
            renderExtraInfo={renderExtraInfo}
            searchValue={searchValue}
            searchPlaceholder={searchPlaceholder}
            setSearchValue={setSearchValue}
            entityLabel={entityLabel}
            onCreateNew={onCreateNew}
            createNewLabel={createNewLabel}
            resultLimit={resultLimit}
            renderRowMeta={renderRowMeta}
            renderRowPrice={renderRowPrice}
            createRows={createRows}
            closeSelector={closeSelector}
            onLoadMore={onLoadMore ? loadMore : undefined}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            pinnedEntityIds={pinnedEntityIds}
            pinnedGroupLabel={pinnedGroupLabel}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
